/**
 * AnalyticsDashboard.js
 *
 * Chatbot workflow analytics dashboard powered by ClickHouse materialized views.
 * Displays KPIs, message trends, channel breakdown, step funnel, hourly heatmap,
 * and user engagement metrics.
 *
 * Props:
 *  - workflowId: Workflow ID to fetch analytics for
 *  - workflowName: Optional display name
 *  - onClose: Close callback
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, MessageSquare, Users, Activity, TrendingUp, TrendingDown,
  RefreshCw, Calendar, X, ChevronDown, Zap, Hash, ArrowRight,
  Clock, Globe, Layers
} from 'lucide-react';
import { apiCallJson } from './Api';

// ==================== Constants ====================
const PERIOD_OPTIONS = [
  { label: '7d', value: 7 },
  { label: '14d', value: 14 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const CHANNEL_COLORS = {
  web: '#3b82f6',
  whatsapp: '#25d366',
  telegram: '#0088cc',
  instagram: '#e4405f',
  facebook: '#1877f2',
  sms: '#6b7280',
  api: '#8b5cf6',
};

const HEATMAP_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ==================== Utility: format large numbers ====================
const fmtNum = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

// ==================== Mini SVG Sparkline ====================
const Sparkline = ({ data, width = 120, height = 32, color = '#3b82f6' }) => {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `0,${height} ${points} ${width},${height}`;

  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <defs>
        <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#grad-${color.replace('#','')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ==================== KPI Card ====================
const KpiCard = ({ icon: Icon, label, value, trend, sparkData, color = '#3b82f6', iconBg = 'bg-blue-50' }) => {
  const trendColor = trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-500' : 'text-gray-400';
  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Activity;

  return (
    <div className="bg-white rounded-xl border border-gray-200/80 p-5 flex flex-col justify-between hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${iconBg}`}>
          <Icon className="h-4.5 w-4.5" style={{ color }} />
        </div>
        {sparkData && <Sparkline data={sparkData} color={color} width={80} height={28} />}
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900 tracking-tight">{fmtNum(value)}</div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
          {trend !== null && trend !== undefined && (
            <span className={`flex items-center text-xs font-semibold ${trendColor}`}>
              <TrendIcon className="h-3 w-3 mr-0.5" />
              {Math.abs(trend)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Simple Bar Chart (SVG) ====================
const BarChartSVG = ({ data, xKey, yKey, color = '#3b82f6', height = 180, barRadius = 3, label = '' }) => {
  if (!data || data.length === 0) return <EmptyState text={`No ${label} data`} />;
  const max = Math.max(...data.map(d => d[yKey]), 1);
  const barWidth = Math.max(4, Math.min(24, (600 / data.length) - 4));

  return (
    <div className="w-full overflow-x-auto">
      <svg width="100%" height={height + 30} viewBox={`0 0 ${Math.max(data.length * (barWidth + 4) + 40, 200)} ${height + 30}`} className="min-w-0">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1="30" y1={height - pct * height + 5} x2="100%" y2={height - pct * height + 5}
                stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray={i === 0 ? '' : '4,4'} />
        ))}
        {data.map((d, i) => {
          const barH = (d[yKey] / max) * height;
          const x = 35 + i * (barWidth + 4);
          return (
            <g key={i}>
              <rect x={x} y={height - barH + 5} width={barWidth} height={barH}
                    rx={barRadius} fill={color} opacity="0.85">
                <title>{d[xKey]}: {fmtNum(d[yKey])}</title>
              </rect>
              {data.length <= 15 && (
                <text x={x + barWidth / 2} y={height + 20} textAnchor="middle"
                      className="text-[9px] fill-gray-400">{(d[xKey] || '').toString().slice(-5)}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ==================== Donut Chart ====================
const DonutChart = ({ data, colorMap, size = 160 }) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const cx = size / 2, cy = size / 2, r = size * 0.38, strokeW = size * 0.14;
  let cumAngle = -90;

  const slices = data.map((d) => {
    const angle = (d.value / total) * 360;
    const start = cumAngle;
    cumAngle += angle;
    return { ...d, start, angle };
  });

  const arcPath = (startAngle, angle) => {
    const r1 = (startAngle * Math.PI) / 180;
    const r2 = ((startAngle + angle) * Math.PI) / 180;
    const x1 = cx + r * Math.cos(r1), y1 = cy + r * Math.sin(r1);
    const x2 = cx + r * Math.cos(r2), y2 = cy + r * Math.sin(r2);
    const large = angle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} className="flex-shrink-0">
        {slices.map((s, i) => (
          <path key={i} d={arcPath(s.start, Math.max(s.angle - 1, 0.5))} fill="none"
                stroke={s.color} strokeWidth={strokeW} strokeLinecap="round">
            <title>{s.label}: {fmtNum(s.value)}</title>
          </path>
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle" className="text-lg font-bold fill-gray-800">{fmtNum(total)}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="text-[10px] fill-gray-400 uppercase">total</text>
      </svg>
      <div className="space-y-1.5 min-w-0">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
            <span className="text-gray-600 truncate">{d.label}</span>
            <span className="font-semibold text-gray-800 ml-auto">{fmtNum(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== Heatmap ====================
const HeatmapChart = ({ data }) => {
  if (!data || data.length === 0) return <EmptyState text="No heatmap data" />;
  const max = Math.max(...data.map(d => d.count), 1);

  // Build 7x24 grid
  const grid = {};
  data.forEach(d => { grid[`${d.day}-${d.hour}`] = d.count; });

  const cellColor = (val) => {
    if (!val) return '#f9fafb';
    const intensity = Math.min(val / max, 1);
    const r = Math.round(59 + (255 - 59) * (1 - intensity));
    const g = Math.round(130 + (255 - 130) * (1 - intensity));
    const b = Math.round(246 + (255 - 246) * (1 - intensity));
    return `rgb(${r},${g},${b})`;
  };

  return (
    <div className="overflow-x-auto">
      <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: `40px repeat(24, 1fr)` }}>
        {/* Header row */}
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-center text-[9px] text-gray-400 pb-1">
            {h % 3 === 0 ? `${h}h` : ''}
          </div>
        ))}
        {/* Data rows */}
        {HEATMAP_DAYS.map((day, di) => (
          <React.Fragment key={day}>
            <div className="text-[10px] text-gray-500 font-medium pr-2 flex items-center justify-end">{day}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const val = grid[`${di + 1}-${h}`] || 0;
              return (
                <div key={h} className="w-full aspect-square rounded-sm cursor-default"
                     style={{ backgroundColor: cellColor(val), minWidth: '14px' }}
                     title={`${day} ${h}:00 — ${val} messages`} />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ==================== Step Funnel ====================
const StepFunnel = ({ steps, nodeLabels }) => {
  if (!steps || steps.length === 0) return <EmptyState text="No step data yet" />;
  const maxSessions = Math.max(...steps.map(s => s.sessions), 1);

  return (
    <div className="space-y-1.5">
      {steps.slice(0, 12).map((step, i) => {
        const pct = (step.sessions / maxSessions) * 100;
        const label = nodeLabels[step.step_id] || step.step_id;
        return (
          <div key={step.step_id} className="group">
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="text-gray-700 font-medium truncate max-w-[60%]" title={step.step_id}>
                {i + 1}. {label}
              </span>
              <span className="text-gray-500">{fmtNum(step.sessions)} sessions</span>
            </div>
            <div className="h-5 bg-gray-100 rounded-md overflow-hidden">
              <div className="h-full rounded-md transition-all duration-500 flex items-center px-2"
                   style={{
                     width: `${Math.max(pct, 3)}%`,
                     background: `linear-gradient(90deg, #3b82f6 0%, ${pct > 50 ? '#2563eb' : '#60a5fa'} 100%)`
                   }}>
                <span className="text-[10px] font-bold text-white/90 whitespace-nowrap">{Math.round(pct)}%</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== Area Chart (Messages Over Time) ====================
const AreaChart = ({ data, height = 200 }) => {
  if (!data || data.length < 2) return <EmptyState text="Not enough data for chart" />;

  const padding = { top: 10, right: 10, bottom: 25, left: 45 };
  const w = 600, h = height;
  const plotW = w - padding.left - padding.right;
  const plotH = h - padding.top - padding.bottom;

  const maxY = Math.max(...data.map(d => d.messages), 1);
  const xScale = (i) => padding.left + (i / (data.length - 1)) * plotW;
  const yScale = (v) => padding.top + plotH - (v / maxY) * plotH;

  const userPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yScale(d.user_messages || 0)}`).join(' ');
  const botPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yScale(d.bot_messages || 0)}`).join(' ');
  const totalPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yScale(d.messages)}`).join(' ');

  const totalArea = `M${xScale(0)},${yScale(0)} ${data.map((d, i) => `L${xScale(i)},${yScale(d.messages)}`).join(' ')} L${xScale(data.length-1)},${yScale(0)} Z`;

  // Y-axis labels
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(maxY * p));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* Grid */}
      {yTicks.map((tick, i) => (
        <g key={i}>
          <line x1={padding.left} y1={yScale(tick)} x2={w - padding.right} y2={yScale(tick)}
                stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray={i === 0 ? '' : '3,3'} />
          <text x={padding.left - 6} y={yScale(tick) + 3} textAnchor="end" className="text-[9px] fill-gray-400">
            {fmtNum(tick)}
          </text>
        </g>
      ))}

      {/* X-axis labels */}
      {data.filter((_, i) => i % Math.ceil(data.length / 6) === 0 || i === data.length - 1).map((d, i) => {
        const idx = data.indexOf(d);
        return (
          <text key={i} x={xScale(idx)} y={h - 4} textAnchor="middle" className="text-[9px] fill-gray-400">
            {d.date?.slice(5)}
          </text>
        );
      })}

      {/* Area + Lines */}
      <path d={totalArea} fill="url(#areaGrad)" />
      <path d={totalPath} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
      <path d={userPath} fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4,2" />
      <path d={botPath} fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4,2" />

      {/* Dots on last point */}
      {data.length > 0 && (
        <>
          <circle cx={xScale(data.length-1)} cy={yScale(data[data.length-1].messages)} r="3" fill="#3b82f6" />
          <circle cx={xScale(data.length-1)} cy={yScale(data[data.length-1].user_messages || 0)} r="2.5" fill="#10b981" />
          <circle cx={xScale(data.length-1)} cy={yScale(data[data.length-1].bot_messages || 0)} r="2.5" fill="#8b5cf6" />
        </>
      )}
    </svg>
  );
};

// ==================== Empty State ====================
const EmptyState = ({ text = 'No data available' }) => (
  <div className="flex flex-col items-center justify-center py-8 text-gray-400">
    <BarChart3 className="h-8 w-8 mb-2 opacity-40" />
    <span className="text-sm">{text}</span>
  </div>
);

// ==================== Section Card ====================
const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-200/80 overflow-hidden ${className}`}>
    <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-gray-400" />}
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ==================== Main Dashboard ====================
const AnalyticsDashboard = ({ workflowId, workflowName, onClose, nodes = [] }) => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [channels, setChannels] = useState([]);
  const [steps, setSteps] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [engagement, setEngagement] = useState([]);
  const [error, setError] = useState(null);

  // Build node label map
  const nodeLabels = useMemo(() => {
    const map = {};
    (nodes || []).forEach(n => {
      const label = n.data?.label || n.data?.stepData?.name || n.id;
      map[n.id] = label;
    });
    return map;
  }, [nodes]);

  const fetchAll = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const [ov, ch, st, hm, en] = await Promise.allSettled([
        apiCallJson(`/api/chatbot/workflows/${workflowId}/analytics/overview?days=${days}`),
        apiCallJson(`/api/chatbot/workflows/${workflowId}/analytics/channels?days=${days}`),
        apiCallJson(`/api/chatbot/workflows/${workflowId}/analytics/steps?days=${days}`),
        apiCallJson(`/api/chatbot/workflows/${workflowId}/analytics/hourly?days=${Math.min(days, 30)}`),
        apiCallJson(`/api/chatbot/workflows/${workflowId}/analytics/engagement?days=${days}`),
      ]);

      if (ov.status === 'fulfilled') setOverview(ov.value);
      if (ch.status === 'fulfilled') setChannels(ch.value.channels || []);
      if (st.status === 'fulfilled') setSteps(st.value.steps || []);
      if (hm.status === 'fulfilled') setHeatmap(hm.value.heatmap || []);
      if (en.status === 'fulfilled') setEngagement(en.value.engagement || []);
    } catch (e) {
      setError(e.message);
    }

    setLoading(false);
    setRefreshing(false);
  }, [workflowId, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const daily = overview?.daily || [];
  const totals = overview?.totals || {};
  const trends = overview?.trends || {};

  // Sparkline data arrays
  const msgSpark = daily.map(d => d.messages);
  const sessSpark = daily.map(d => d.sessions);
  const userSpark = daily.map(d => d.users);

  // Donut data
  const donutData = channels.map(c => ({
    label: c.channel,
    value: c.messages,
    color: CHANNEL_COLORS[c.channel] || '#9ca3af'
  }));

  return (
    <div className="h-full flex flex-col bg-gray-50/80">
      {/* ========== Header ========== */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Analytics</h2>
              <p className="text-xs text-gray-500">{workflowName || workflowId}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Period selector */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDays(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    days === opt.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => fetchAll(false)}
              disabled={refreshing}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {onClose && (
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ========== Body ========== */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            Failed to load analytics: {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-6 w-6 text-blue-500 animate-spin" />
            <span className="ml-2 text-gray-500 text-sm">Loading analytics...</span>
          </div>
        ) : (
          <>
            {/* ========== KPI Cards ========== */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                icon={MessageSquare} label="Total Messages" value={totals.messages}
                trend={trends.messages} sparkData={msgSpark}
                color="#3b82f6" iconBg="bg-blue-50"
              />
              <KpiCard
                icon={Zap} label="Sessions" value={totals.sessions}
                trend={trends.sessions} sparkData={sessSpark}
                color="#8b5cf6" iconBg="bg-violet-50"
              />
              <KpiCard
                icon={Users} label="Unique Users" value={totals.users}
                trend={trends.users} sparkData={userSpark}
                color="#10b981" iconBg="bg-emerald-50"
              />
              <KpiCard
                icon={Activity} label="Avg Msgs/Session" 
                value={totals.sessions ? Math.round(totals.messages / totals.sessions * 10) / 10 : 0}
                trend={null} sparkData={null}
                color="#f59e0b" iconBg="bg-amber-50"
              />
            </div>

            {/* ========== Messages Over Time + Channels ========== */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <Section title="Messages Over Time" icon={TrendingUp} className="xl:col-span-2">
                <div className="flex items-center gap-4 mb-3 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-blue-500 rounded-full inline-block" /> Total
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-emerald-500 rounded-full inline-block" style={{ borderBottom: '1px dashed #10b981' }} /> User
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-violet-500 rounded-full inline-block" style={{ borderBottom: '1px dashed #8b5cf6' }} /> Bot
                  </span>
                </div>
                <AreaChart data={daily} height={200} />
              </Section>

              <Section title="Channel Breakdown" icon={Globe}>
                {donutData.length > 0 ? (
                  <DonutChart data={donutData} colorMap={CHANNEL_COLORS} />
                ) : (
                  <EmptyState text="No channel data" />
                )}
              </Section>
            </div>

            {/* ========== Step Funnel + Heatmap ========== */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Section title="Step Funnel" icon={Layers}>
                <StepFunnel steps={steps} nodeLabels={nodeLabels} />
              </Section>

              <Section title="Hourly Activity" icon={Clock}>
                <HeatmapChart data={heatmap} />
              </Section>
            </div>

            {/* ========== Engagement Over Time ========== */}
            <Section title="Daily Active Users" icon={Users}>
              <BarChartSVG
                data={engagement}
                xKey="date"
                yKey="active_users"
                color="#10b981"
                height={160}
                label="engagement"
              />
            </Section>

            {/* ========== Raw Numbers Table ========== */}
            {channels.length > 0 && (
              <Section title="Channel Details" icon={Hash}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                        <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Messages</th>
                        <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                        <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                        <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Msg/Session</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {channels.map(c => (
                        <tr key={c.channel} className="hover:bg-gray-50/50">
                          <td className="py-2.5 flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CHANNEL_COLORS[c.channel] || '#9ca3af' }} />
                            <span className="font-medium text-gray-800 capitalize">{c.channel}</span>
                          </td>
                          <td className="text-right py-2.5 text-gray-600 tabular-nums">{fmtNum(c.messages)}</td>
                          <td className="text-right py-2.5 text-gray-600 tabular-nums">{fmtNum(c.sessions)}</td>
                          <td className="text-right py-2.5 text-gray-600 tabular-nums">{fmtNum(c.users)}</td>
                          <td className="text-right py-2.5 text-gray-600 tabular-nums">
                            {c.sessions ? (c.messages / c.sessions).toFixed(1) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
